import React from "react";
import {
  Box,
  Container,
  Divider,
  Grid,
  Stack,
  Typography
} from "@mui/material";
import FooterCommonLogin from "./footerCommonLogin";

const accent = "#3A1C73";
const border = "#E4E4E4";
const subdued = "#9E9E9E";

const categoriesData = {
  "fuel-station": {
    documents: [
      "Detail of the material for pavement composition for deceleration lane, service road and acceleration lane.",
      "Detailed site map from State Revenue Department showing the location of proposed plot and existing NH/ Certificate that the proposed plot location directly abutts the NH.",
      "Letter of Intent issued by Oil Company",
      "Land Ownership, Lease Agreement, Khata certificate/Record of rights",
      "General Power of Attorney given by Oil Company for signing the License Deed",
      "If Letter of intent (LOI) holder is not landowner, then give lease agreement from landowner to LOI holder for leasing of land on which Retail fuel station is located",
      "Documentary evidence of additional Land Acquisition details (if required) beyond the available ROW (to accommodate service roads, deceleration / acceleration lanes, turning radius etc.) done by the owner of the fuel station.",
      "Fuel station is a Company Owned and Company Operated (COCO) fuel station, please upload lease agreement given by Landowner to Oil Company",
      "Submit reach type document to verify the stretch reach.",
      "Proposed Site Pictures",
      "Any Document to indicate commercial activities are allowed on the land.",
      "Interception Drain Certificate/ Rain Water Drain Design"
    ],
    drawings: [
      "Certified copy of location plan of the Fuel Station along the National Highway showing details of Right of Way (ROW) of National Highway, existing intersections and the intersecting roads, including existing public roads and other developments, falling within a reach of 1.1 km on each side of the Fuel Station and carriageway",
      "Certified copy of plan of the proposed Fuel Station showing details of deceleration, acceleration lanes, service road (if provided), buffer strip, fuel pump, office, kiosk, lubricorium, air and water supply, toilet & drinking water facilities, drainage details, signages and markings conforming to applicable figures enclosed with these norms",
      "Certified copy of sectional and layout plan showing the elevation of the Fuel Station with respect to the National Highway, proposed slopes for adequate drainage and prevention of water logging, along with detailed chainage, location, public facility arrangements of access (including service road, deceleration/acceleration lanes), and complete geometrics for safe traffic movement. (required)",
      "Drainage plan for the Fuel Station/ Wayside Amenities.",
      "Detailed drawing for augmented/ widening of existing cross drainage structure/ culverts as per site requirement",
      "Certified copy of drawing showing requirement as per norms i.e. Location, Layout Road, Road Marking, Drainage Plan & Crust Composition of access road, etc. (Only if you select Private Property)",
      "The location drawing shall show the relevant details of the reach for 1.1 km along the National Highway on either side of the property. (Only if you select Private Property)",
      "Drainage plan for the Private Properties/ Other Establishments (Other than fuel stations). (Only if you select Private Property)",
      "Detailed drawing for augmented/ widening of existing cross drainage structure/ culverts as per site requirement. (Only If you select Private Property)"
    ]
  },
  wayside: {
    documents: [
      "GSTIN/CIN/TIN Certificate",
      "Letter of Authorization / General Power of Attorney for signing the License Deed",
      "Letter of Intent (LoI) Copy issued by oil company showing details of Dealer, Issue Date and Validity date (Fuel Station / Wayside Amenities only)",
      "Letter of Authorisation (LoA) for Development of WaySide Amenities (Only for WSA- Fuel Stations as Part of Wayside Amenity.)",
      "If Letter of intent (LOI) holder is not landowner, then give lease agreement from landowner to LOI holder for leasing of land on which Retail fuel station is located (Fuel Station / Wayside Amenities only)",
      "Document for Land Ownership, Lease Agreement, Khata certificate/Record of rights.",
      "Hydraulic Calculations showing sufficiency of drainage Arrangement",
      "Population Certificate from Local Urban Body (Only for Urban Reaches)",
      "Detail of the material for pavement composition for deceleration lane, service road and acceleration lane.",
      "Documentary evidence of additional Land Acquisition details (if required) beyond the available ROW (to accommodate service roads, deceleration / acceleration lanes, turning radius etc.) done by the owner of the fuel station (Fuel Station / Wayside Amenities only)."
    ],
    drawings: [
      "Drainage plan",
      "Strip plan of the proposed site showing details of Right of Way (ROW) of National Highway, existing intersections and the intersecting roads, including existing public roads and other developments, falling within a reach of 1.1 km in each side of the plot.",
      "Layout plan and elevation plan with respect to the National Highway along with detailed chainage, location, public facility arrangements of access (including service road and deceleration/acceleration lanes) and complete geometrics for safe traffic movement",
      "Detailed drawing for augmented/ widening of existing cross drainage structure/ culverts as per site requirement (Only when widening and Augmentation is required)"
    ]
  },
  private: {
    documents: [
      "GSTIN/CIN/TIN Certificate",
      "Letter of Authorization / General Power of Attorney for signing the License Deed",
      "Document for Land Ownership, Lease Agreement, Khata certificate/Record of rights.",
      "Hydraulic Calculations showing sufficiency of drainage Arrangement",
      "Population Certificate from Local Urban Body (Only for Urban Reaches)",
      "Detail of the material for pavement composition for deceleration lane, service road and acceleration lane."
    ],
    drawings: [
      "Drainage plan",
      "Strip plan of the proposed site showing details of Right of Way (ROW) of National Highway, existing intersections and the intersecting roads, including existing public roads and other developments, falling within a reach of 1.1 km in each side of the plot.",
      "Layout plan and elevation plan with respect to the National Highway along with detailed chainage, location, public facility arrangements of access (including service road and deceleration/acceleration lanes) and complete geometrics for safe traffic movement",
      "Detailed drawing for augmented/ widening of existing cross drainage structure/ culverts as per site requirement (Only when widening and Augmentation is required)"
    ]
  }
};

const categories = [
  {
    id: "fuel-station",
    title:
      "Fuel Station Only | Fuel Station/ Commercial Facility | Fuel Station with Restaurant/ Rest Area Complex",
    count: 13
  },
  { id: "wayside", title: "Fuel Station / Wayside Amenities", count: 14 },
  { id: "private", title: "Private Property / Other establishments", count: 10 }
];

const DocListAccessPermission: React.FC = () => {
  const [activeIndex, setActiveIndex] = React.useState<number>(0);

  const activeCategory = categories[activeIndex];
  const activeData =
    categoriesData[activeCategory.id as keyof typeof categoriesData];

  return (
    <>
      {/* Hero / Banner */}
      <Box
        sx={{
          backgroundImage: `linear-gradient(rgba(15,23,42,0.55), rgba(15,23,42,0.10)), url(${require("@assets/images/common-login-bg.png")})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          minHeight: { xs: 120, md: 140 },
          display: "flex",
          alignItems: "center"
        }}
      >
        <Container
          maxWidth={false}
          disableGutters
          sx={{
            width: "100%",
            maxWidth: "100%",
            px: { xs: 2, md: 6 },
            py: { xs: 4, md: 6 }
          }}
        >
          <Typography
            variant="h3"
            sx={{
              color: "#FFF",
              fontWeight: 800,
              fontSize: { xs: 24, md: 32 },
              lineHeight: 1.25
            }}
          >
            Document List for Access Permission
          </Typography>
        </Container>
      </Box>

      {/* Content */}
      <Box sx={{ bgcolor: "#FFFFFF" }}>
        <Container maxWidth={false} disableGutters sx={{
          width: "100%",
          maxWidth: "100%",
          px: { xs: 2, md: 6 },
          py: { xs: 2 }
        }}>

          {/* ===== SECTION 1: Required Details ===== */}
          <Typography variant="h3" sx={{ mb: 2, fontWeight: "700", pt: 2, fontSize: { xs: "1.8rem", md: "2.5rem" } }}>
            Required Details for Access Permission
          </Typography>
          <Typography sx={{ mb: 1, fontSize: { xs: 14, md: 16 } }} className="gray-800">
            Before starting your application, please keep the following information readily available:
          </Typography>
          <Divider sx={{ borderColor: border, mb: 3 }} />

          {/* Information List */}
          <Box
            component="ul"
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              listStyle: "none",
              p: 0,
              m: 0
            }}
          >
            {/* Applicant Information */}
            <Box component="li">
              <Typography className="gray-800" variant="h6" sx={{ fontWeight: "700", fontSize: { xs: "1.1rem", md: "1.25rem" } }}>
                1. Applicant Information
              </Typography>
              <Box component="ul" sx={{ listStyle: "none", p: 0, pl: 3, m: 0, mt: 0.5 }}>
                <Box component="li" sx={{ display: "flex", alignItems: "flex-start", gap: 1, py: 0.5 }}>
                  <Typography className="gray-800" variant="body1" sx={{ fontWeight: 400, color: "text.secondary", fontSize: { xs: "1rem", md: "1.1rem" } }}>•</Typography>
                  <Typography className="gray-800" variant="body1" sx={{ fontSize: { xs: "1rem", md: "1.1rem" } }}>Applicant Type (Individual/Organization)</Typography>
                </Box>
                <Box component="li" sx={{ display: "flex", alignItems: "flex-start", gap: 1, py: 0.5 }}>
                  <Typography className="gray-800" variant="body1" sx={{ fontWeight: 400, color: "text.secondary", fontSize: { xs: "1rem", md: "1.1rem" } }}>•</Typography>
                  <Typography className="gray-800" variant="body1" sx={{ fontSize: { xs: "1rem", md: "1.1rem" } }}>Email Address</Typography>
                </Box>
              </Box>
            </Box>

            {/* Application Information */}
            <Box component="li">
              <Typography className="gray-800" variant="h6" sx={{ fontWeight: "700", fontSize: { xs: "1.1rem", md: "1.25rem" } }}>
                2. Application Information
              </Typography>
              <Box component="ul" sx={{ listStyle: "none", p: 0, pl: 3, m: 0, mt: 0.5 }}>
                <Box component="li" sx={{ display: "flex", alignItems: "flex-start", gap: 1, py: 0.5 }}>
                  <Typography className="gray-800" variant="body1" sx={{ fontWeight: 400, color: "text.secondary", fontSize: { xs: "1rem", md: "1.1rem" } }}>•</Typography>
                  <Typography className="gray-800" variant="body1" sx={{ fontSize: { xs: "1rem", md: "1.1rem" } }}>Access Permission Category</Typography>
                </Box>
                <Box component="li" sx={{ display: "flex", alignItems: "flex-start", gap: 1, py: 0.5 }}>
                  <Typography className="gray-800" variant="body1" sx={{ fontWeight: 400, color: "text.secondary", fontSize: { xs: "1rem", md: "1.1rem" } }}>•</Typography>
                  <Typography className="gray-800" variant="body1" sx={{ fontSize: { xs: "1rem", md: "1.1rem" } }}>Property Type (Fuel Station, Private Property)</Typography>
                </Box>
              </Box>
            </Box>

            {/* Location Information */}
            <Box component="li">
              <Typography className="gray-800" variant="h6" sx={{ fontWeight: "700", fontSize: { xs: "1.1rem", md: "1.25rem" } }}>
                3. Location Information
              </Typography>
              <Box component="ul" sx={{ listStyle: "none", p: 0, pl: 3, m: 0, mt: 0.5 }}>
                {[
                  "Proposed Site Location",
                  "National Highway Number",
                  "Highway Authority",
                  "State",
                  "District",
                  "Village/Town",
                  "Taluka/Tehsil",
                  "PIN Code",
                  "Property Address"
                ].map((item, idx) => (
                  <Box component="li" key={idx} sx={{ display: "flex", alignItems: "flex-start", gap: 1, py: 0.5 }}>
                    <Typography className="gray-800" variant="body1" sx={{ fontWeight: 400, color: "text.secondary", fontSize: { xs: "1rem", md: "1.1rem" } }}>•</Typography>
                    <Typography className="gray-800" variant="body1" sx={{ fontSize: { xs: "1rem", md: "1.1rem" } }}>{item}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            {/* GIS & Highway Information */}
            <Box component="li">
              <Typography className="gray-800" variant="h6" sx={{ fontWeight: "700", fontSize: { xs: "1.1rem", md: "1.25rem" } }}>
                4. GIS & Highway Information
              </Typography>
              <Box component="ul" sx={{ listStyle: "none", p: 0, pl: 3, m: 0, mt: 0.5 }}>
                {[
                  "Proposed Site Location on GIS Map",
                  "Exact Pin Location",
                  "Highway Stretch",
                  "Side of Highway (LHS/RHS)",
                  "Chainage Details"
                ].map((item, idx) => (
                  <Box component="li" key={idx} sx={{ display: "flex", alignItems: "flex-start", gap: 1, py: 0.5 }}>
                    <Typography className="gray-800" variant="body1" sx={{ fontWeight: 400, color: "text.secondary", fontSize: { xs: "1rem", md: "1.1rem" } }}>•</Typography>
                    <Typography className="gray-800" variant="body1" sx={{ fontSize: { xs: "1rem", md: "1.1rem" } }}>{item}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Property Information */}
            <Box component="li">
              <Typography className="gray-800" variant="h6" sx={{ fontWeight: "700", fontSize: { xs: "1.1rem", md: "1.25rem" } }}>
                5. Property Information
              </Typography>
              <Box component="ul" sx={{ listStyle: "none", p: 0, pl: 3, m: 0, mt: 0.5 }}>
                {[
                  "Letter of Intent (LOI) Details (where applicable)",
                  "Plot Frontage",
                  "Plot Depth",
                  "Plot Area"
                ].map((item, idx) => (
                  <Box component="li" key={idx} sx={{ display: "flex", alignItems: "flex-start", gap: 1, py: 0.5 }}>
                    <Typography className="gray-800" variant="body1" sx={{ fontWeight: 400, color: "text.secondary", fontSize: { xs: "1rem", md: "1.1rem" } }}>•</Typography>
                    <Typography className="gray-800" variant="body1" sx={{ fontSize: { xs: "1rem", md: "1.1rem" } }}>{item}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>

          {/* Note Box */}
          <Box
            sx={{
              mt: 4,
              p: 2.5,
              borderRadius: 2,
              borderLeft: `4px solid ${border}`
            }}
          >
            <Typography variant="body1" className="gray-800" sx={{ fontWeight: 500, fontSize: { xs: "1rem", md: "1.1rem" } }}>
              <strong>Note:</strong> Based on the selected GIS location, the portal may automatically retrieve or pre-fill information such as Highway Number, Highway Category, Chainage, State, District, Village, and Urban/Rural or Mountainous classification, wherever integration is available.
            </Typography>
          </Box>
        </Container>

        <Container
          maxWidth={false}
          disableGutters
          sx={{
            width: "100%",
            maxWidth: "100%",
            px: { xs: 2, md: 6 },
            py: { xs: 2, md: 2 }
          }}
        >
          <Typography variant="h4" sx={{ mb: 2, fontWeight: "700" }}>
            Required Documents For Access Permission
          </Typography>
          <Divider sx={{ borderColor: border }} />
          <Grid container spacing={0} sx={{ display: "flex" }}>
            {/* Left column: Categories */}
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  bgcolor: "#fff",
                  height: "100%",
                  borderRight: `1px solid ${border}`,
                  p: 3
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 700,
                    mb: 2,
                    fontSize: 20,
                    color: "#212121"
                  }}
                >
                  Sub category
                </Typography>

                <Divider sx={{ mb: 2, borderColor: border }} />

                <Stack spacing={1.5}>
                  {categories.map((c, idx) => {
                    const active = idx === activeIndex;
                    return (
                      <Box
                        key={c.id}
                        onClick={() => setActiveIndex(idx)}
                        sx={{
                          position: "relative",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 2,
                          p: "14px 12px",
                          cursor: "pointer",
                          bgcolor: active
                            ? "rgba(58,28,115,0.08)"
                            : "transparent",
                          borderRight: active
                            ? `4px solid ${accent}`
                            : "0px solid transparent",
                          borderRadius: "2px",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            bgcolor: active
                              ? "rgba(58,28,115,0.08)"
                              : "rgba(0,0,0,0.02)"
                          }
                        }}
                      >
                        <Box sx={{ flex: 1, pr: 1 }}>
                          <Typography
                            sx={{
                              fontSize: 14,
                              fontWeight: 700,
                              color: active ? accent : "#333",
                              lineHeight: 1.5
                            }}
                          >
                            {c.title}
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            flexShrink: 0,
                            width: 30,
                            height: 30,
                            borderRadius: "50%",
                            bgcolor: active ? accent : "#D5D5D5",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: active ? "#fff" : "#666",
                            fontSize: 11,
                            fontWeight: 700
                          }}
                        >
                          {c.count}
                        </Box>
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
            </Grid>

            {/* Right column: Document List */}
            <Grid item xs={12} md={8}>
              <Box
                sx={{
                  bgcolor: "#fff",
                  p: 3
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 500,
                    mb: 2,
                    fontSize: 20,
                    color: "#212121",
                    lineHeight: 1.4
                  }}
                >
                  {activeCategory.title}
                </Typography>

                <Divider sx={{ my: 2, borderColor: border }} />

                <Typography
                  sx={{
                    fontWeight: 700,
                    mb: 2,
                    fontSize: 13,
                    color: "#212121"
                  }}
                >
                  Documents List
                </Typography>

                <Stack spacing={0}>
                  {activeData.documents.map((doc, i) => (
                    <Box key={i}>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 2,
                          alignItems: "flex-start",
                          py: 1.5
                        }}
                      >
                        <Typography
                          sx={{
                            minWidth: 24,
                            color: subdued,
                            fontWeight: 600,
                            fontSize: 13
                          }}
                        >
                          {i + 1}.
                        </Typography>
                        <Typography
                          sx={{
                            color: "#333",
                            fontSize: 13,
                            lineHeight: 1.6,
                            fontWeight: 400
                          }}
                        >
                          {doc}
                        </Typography>
                      </Box>
                      {/* <Divider sx={{ borderColor: border, my: 0 }} /> */}
                    </Box>
                  ))}
                </Stack>

                <Divider sx={{ my: 2, borderColor: border }} />

                <Typography
                  sx={{
                    fontWeight: 700,
                    mb: 2,
                    fontSize: 13,
                    color: "#212121"
                  }}
                >
                  Drawings List
                </Typography>

                <Stack spacing={0}>
                  {activeData.drawings.map((draw, i) => (
                    <Box key={i}>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 2,
                          alignItems: "flex-start",
                          py: 1.5
                        }}
                      >
                        <Typography
                          sx={{
                            minWidth: 24,
                            color: subdued,
                            fontWeight: 600,
                            fontSize: 13
                          }}
                        >
                          {i + 1}.
                        </Typography>
                        <Typography
                          sx={{
                            color: "#333",
                            fontSize: 13,
                            lineHeight: 1.6,
                            fontWeight: 400
                          }}
                        >
                          {draw}
                        </Typography>
                      </Box>
                      <Divider sx={{ borderColor: border, my: 0 }} />
                    </Box>
                  ))}
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
      <FooterCommonLogin />
    </>
  );
};

export default DocListAccessPermission;
